import { useEffect, useRef } from "react";
import "./TwisterAnimation.css";

export default function TwisterAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame (960px total ÷ 5 frames)
    const frameHeight = 192; // Height of each frame (assuming it's square)
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Create and position animation element - positioned higher than the target
    const twister = document.createElement("div");
    twister.className = "twister-anim";
    twister.style.setProperty('--tx', `${centerX - frameWidth / 2}px`);
    twister.style.setProperty('--ty', `${centerY - frameHeight / 2 - 60}px`); // Moved up by 60px
    container.appendChild(twister);
    animRef.current = twister;
    
    // Create wind particles around the twister - more particles appear as the twister grows larger
    const createWindParticles = (intensity = 1) => {
      const count = Math.floor(5 * intensity); // More particles as intensity increases
      
      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'twister-particle';
        
        // Position around the twister - wider spread as intensity increases
        const angle = Math.random() * Math.PI * 2;
        const radius = (30 * intensity) + Math.random() * (50 * intensity); 
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        
        particle.style.left = `${x}px`;
        particle.style.top = `${y}px`;
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--scale', 0.4 + Math.random() * 0.6 * intensity);
        
        container.appendChild(particle);
        
        // Auto-remove particles - longer duration as intensity increases
        setTimeout(() => particle.remove(), 300 + Math.random() * 500 * intensity);
      }
    };
    
    // Schedule particle bursts at different intensities as the animation progresses
    const scheduleWindParticles = () => {
      // Early frames - small twister (row 1)
      setTimeout(() => createWindParticles(0.5), 0);     // Frame 1
      setTimeout(() => createWindParticles(0.6), 100);   // Frame 2
      setTimeout(() => createWindParticles(0.7), 200);   // Frame 3
      setTimeout(() => createWindParticles(0.8), 300);   // Frame 4
      setTimeout(() => createWindParticles(0.9), 400);   // Frame 5
      
      // Middle frames - medium twister (row 2)
      setTimeout(() => createWindParticles(1.0), 500);   // Frame 6
      setTimeout(() => createWindParticles(1.2), 600);   // Frame 7
      setTimeout(() => createWindParticles(1.3), 700);   // Frame 8
      setTimeout(() => createWindParticles(1.4), 800);   // Frame 9 - impact
      setTimeout(() => createWindParticles(1.5), 900);   // Frame 10
      
      // Final frames - largest twister (row 3)
      setTimeout(() => createWindParticles(1.7), 1000);  // Frame 11
      setTimeout(() => createWindParticles(1.8), 1100);  // Frame 12
      setTimeout(() => createWindParticles(1.9), 1200);  // Frame 13
      setTimeout(() => createWindParticles(2.0), 1300);  // Frame 14
      setTimeout(() => createWindParticles(1.8), 1400);  // Frame 15
    };
    
    // Schedule all wind particle bursts
    scheduleWindParticles();
    
    // Impact shake at frame 9 (when twister reaches good size in middle of animation)
    setTimeout(() => {
      defenderEl.classList.add('twister-hit');
      
      // Add flash effect
      const flash = document.createElement('div');
      flash.className = 'twister-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Remove flash
      setTimeout(() => {
        flash.remove();
        defenderEl.classList.remove('twister-hit');
      }, 400);
    }, 800); // Timed to hit when twister is at a good size (frame 9)

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (twister.parentNode) {
        twister.remove();
      }
      onComplete();
    }, 1500); // Full duration of the 15-frame animation

    // Cleanup function
    return () => {
      clearTimeout(completeTimer);
      
      if (defenderEl) {
        defenderEl.classList.remove('twister-hit');
      }
      
      if (twister.parentNode) {
        twister.remove();
      }
      
      // Remove any flash or particles
      const elements = container.querySelectorAll('.twister-flash, .twister-particle');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

TwisterAnimation.moveType = "dragon";
